import { CALC_SOURCES, COLUMN_CATALOG } from '../lib/constants'

export const SORT_DIRECTION_OPTIONS = [
  { value: 'desc', label: 'Descending (highest first)' },
  { value: 'asc', label: 'Ascending (lowest first)' }
]

export const DRAG_TYPE = 'COLUMN_ITEM'

/** Detail panel section titles for derived column dependencyOptions[calcSource] */
export const DERIVED_DEP_SOURCE_SECTION_TITLE = {
  marketValue: 'Market value',
  aip: 'Alternatives investment profile (AIP)'
}

export function groupCatalogByCategory () {
  const groups = []
  const seen = new Map()
  for (const entry of COLUMN_CATALOG) {
    if (!seen.has(entry.category)) {
      const group = { category: entry.category, items: [] }
      seen.set(entry.category, group)
      groups.push(group)
    }
    seen.get(entry.category).items.push(entry)
  }
  return groups
}

export const CATALOG_GROUPS = groupCatalogByCategory()

export function uniqueCalcSourcesFromInputs (calcInputs) {
  const seen = new Set()
  const out = []
  for (const input of calcInputs || []) {
    if (input?.source && !seen.has(input.source)) {
      seen.add(input.source)
      out.push(input.source)
    }
  }
  return out
}

/**
 * Resolves which source settings are exposed in the dep-override panel for a
 * derived column. Drives the "inheriting vs configured" model:
 *
 * - Catalog without `dependencyOverrides` (or source not in the map) -> inherit
 *   the full `source.settingsSchema`. Inheriting derived columns (DVPI, MOIC)
 *   require no declaration; this is the implicit default.
 * - Catalog with `dependencyOverrides[sourceName]` as an array -> expose only
 *   the listed setting keys. Configured derived columns that own a setting at
 *   their column-level `settingsSchema` (e.g. mvChange owns `activityTypes`)
 *   should NOT list that setting here, so the dep panel doesn't double-expose
 *   or footgun it.
 * - Empty array -> no settings (date control still appears if `supportsDate`).
 */
export function effectiveSourceSettings (sourceName, source, catalog) {
  const overrides = catalog?.dependencyOverrides
  if (!overrides || overrides[sourceName] === undefined) {
    return source?.settingsSchema || []
  }
  const allowed = new Set(overrides[sourceName])
  return (source?.settingsSchema || []).filter(s => allowed.has(s.key))
}

/**
 * True if at least one calc source contributes anything to the dep-override
 * panel (a date control via `supportsDate` or one or more effective settings).
 */
export function derivedHasOverrideableSources (uniqueSources, catalog) {
  for (const sourceName of uniqueSources) {
    const src = CALC_SOURCES[sourceName]
    if (!src) continue
    if (src.supportsDate) return true
    if (effectiveSourceSettings(sourceName, src, catalog).length > 0) return true
  }
  return false
}

export function getDateHint (options) {
  if (!options?.dateMode || options.dateMode === 'default') return null
  if (options.dateMode === 'quickDate' && options.quickDate) return options.quickDate
  if (options.dateMode === 'dateRange') return 'Custom'
  return null
}

export function parseDateModeValue (value) {
  if (!value || value === 'default') return { dateMode: 'default', quickDate: null }
  if (value === 'dateRange') return { dateMode: 'dateRange', quickDate: null }
  if (value.startsWith('qd:')) return { dateMode: 'quickDate', quickDate: value.slice(3) }
  return { dateMode: 'default', quickDate: null }
}

export function toDateModeSelectValue (options) {
  if (!options?.dateMode || options.dateMode === 'default') return 'default'
  if (options.dateMode === 'dateRange') return 'dateRange'
  if (options.dateMode === 'quickDate' && options.quickDate) return `qd:${options.quickDate}`
  return 'default'
}
